import dayjs from "dayjs";
import { COLLAPSIBLE_GROUPS } from "./CollapsibleHeaderGroup";

const PLACEHOLDER_PREFIX = "__placeholder_";

export const LOGISTICS_DATE_FIELDS = new Set([
  "PODate",
  "BookingDone",
  "ETA",
  "DocumentsApproved",
  "OriginalTelexDocsSemt",
]);

export const CP_SORT_FIELD_OPTIONS = [
  { id: "__default__", labelKey: "sortByDefault" },
  { id: "FechaAceptacion", labelKey: "sortByAccepted" },
  ...[
    ...new Set(
      Object.values(COLLAPSIBLE_GROUPS)
        .flat()
        .filter((f) => !String(f).startsWith(PLACEHOLDER_PREFIX)),
    ),
  ].map((id) => ({ id })),
];

export function normalizeTaskKey(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

export function pickTimelineCell(row, taskName) {
  if (!row || typeof row !== "object") return null;
  const direct = row[taskName];
  if (direct && typeof direct === "object" && !Array.isArray(direct)) {
    return direct;
  }
  const want = normalizeTaskKey(taskName);
  if (!want) return null;
  for (const k of Object.keys(row)) {
    if (normalizeTaskKey(k) === want) {
      const cell = row[k];
      if (cell && typeof cell === "object" && !Array.isArray(cell)) return cell;
    }
  }
  return null;
}

function normalizeTaskCellDates(taskObj) {
  if (!taskObj || typeof taskObj !== "object") return null;
  const pick = (a, b) => (a !== undefined && a !== null ? a : b);
  return {
    RealDate: pick(taskObj.RealDate, taskObj.realdate),
    UpdatedDate: pick(taskObj.UpdatedDate, taskObj.updateddate),
    ExpectedDate: pick(taskObj.ExpectedDate, taskObj.expecteddate),
  };
}

/** Timestamp; misma prioridad que TaskCell: Real → Updated → Expected. */
export function getTaskSortTimestamp(taskObj) {
  const cell = normalizeTaskCellDates(taskObj);
  if (!cell) return null;
  const dateStr = cell.RealDate || cell.UpdatedDate || cell.ExpectedDate;
  if (!dateStr) return null;
  const d = dayjs(dateStr);
  return d.isValid() ? d.valueOf() : null;
}

export function getTimelineColumnSortTimestamp(row, task) {
  return getTaskSortTimestamp(pickTimelineCell(row, task));
}

function getMuestraTaskSortTimestamp(row, taskName) {
  if (!Array.isArray(row?.TaskStatus)) return null;
  const want = normalizeTaskKey(taskName);
  const st = row.TaskStatus.find((t) => normalizeTaskKey(t?.name) === want);
  if (!st) return null;
  const dateStr = st.real || st.updated || st.expected || st.createdAt;
  if (!dateStr) return null;
  const d = dayjs(dateStr);
  return d.isValid() ? d.valueOf() : null;
}

export function getLogisticsSortTimestamp(row, field) {
  const values = row?._logisticsMap?.[field];
  if (Array.isArray(values) && values.length > 0) {
    let best = null;
    for (const v of values) {
      const d = dayjs(v);
      if (d.isValid()) {
        const t = d.valueOf();
        if (best == null || t < best) best = t;
      }
    }
    if (best != null) return best;
  }
  if (!Array.isArray(row?.logistics)) return null;
  let best = null;
  for (const entry of row.logistics) {
    const v = entry?.[field];
    if (!v) continue;
    const d = dayjs(v);
    if (d.isValid()) {
      const t = d.valueOf();
      if (best == null || t < best) best = t;
    }
  }
  return best;
}

/** Orden por defecto del mini-Gantt (ETA → CUT-OFF → ETDF → aceptación). */
export function getDefaultSortTimestamp(row) {
  const candidates = [
    getLogisticsSortTimestamp(row, "ETA"),
    getTimelineColumnSortTimestamp(row, "CUT-OFF"),
    getTimelineColumnSortTimestamp(row, "ETDF"),
    row?.FechaAceptacion,
    row?.FECHA_PREVISTA,
    row?.createdAt,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    const d = dayjs(c);
    if (d.isValid()) return d.valueOf();
  }
  return null;
}

export function getRowSortTimestamp(row, field) {
  if (!row) return null;
  if (field === "__default__") return getDefaultSortTimestamp(row);
  if (field === "FechaAceptacion") {
    const d = dayjs(
      row.FechaAceptacion || row.FECHA_PREVISTA || row.createdAt,
    );
    return d.isValid() ? d.valueOf() : null;
  }
  if (LOGISTICS_DATE_FIELDS.has(field)) {
    return getLogisticsSortTimestamp(row, field);
  }
  const timeline = getTimelineColumnSortTimestamp(row, field);
  if (timeline != null) return timeline;
  if (row.ID_MUESTRA != null) return getMuestraTaskSortTimestamp(row, field);
  return null;
}

export function compareNullableTimestamps(v1, v2) {
  if (v1 == null && v2 == null) return 0;
  if (v1 == null) return 1;
  if (v2 == null) return -1;
  return v1 - v2;
}

export function sortCriticalPathRows(rows, sortField, sortDirection = "asc") {
  const dir = sortDirection === "desc" ? -1 : 1;
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const cmp = compareNullableTimestamps(
      getRowSortTimestamp(a, sortField),
      getRowSortTimestamp(b, sortField),
    );
    return cmp * dir;
  });
}
